import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { EstadosUsuario } from 'src/app/shared/enums/estados-usuario.enum';
import { UserRoles } from 'src/app/shared/enums/roles-enum';
import { ActionsService } from 'src/app/shared/services/actions.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { BinanceDepositService } from '../../services/binance-deposit.service';
import {
  BalanceResponse,
  BinanceWithdrawalService,
  WithdrawalHistoryItem,
} from '../../services/binance-withdrawal.service';
import { CartolaService } from '../../services/cartola.service';
import { WalletVesClientService, VesMovementItem, UsdtCardexItem, UsdtWithdrawalItem, UsdtPendingSwapItem } from '../../services/wallet-ves-client.service';
import { VitaWalletService } from '../../services/vita-wallet.service';
import { VitaCalculatorService } from '../../services/vita-calculator.service';
import { DataUpdateRequestService } from '../../services/data-update-request.service';
import {
  CupoRegistroService,
  CupoRegistroMe,
} from '../../services/cupo-registro.service';
import {
  leerEstadoCompliance,
  operaComoAprobado,
} from 'src/app/shared/utils/estado-compliance.util';
import { loadUser } from 'src/app/auth/auth.actions';
import { esTitularVesChileno, tieneSaldoVes } from 'src/app/shared/utils/saldo-ves';

export interface UnifiedUsdtMovement {
  source: 'cardex' | 'withdrawal';
  date: string;
  label: string;
  amount: number;
  isCredit: boolean;
  status: string;
  // cardex fields
  haber?: number;
  debe?: number;
  saldo?: number;
  description?: string | null;
  network?: string;
  // withdrawal fields
  walletAddress?: string;
  fee?: number;
  netAmount?: number;
  binanceTxId?: string | null;
  rejectionReason?: string | null;
}

@Component({
  selector: 'app-user-home',
  templateUrl: './user-home.component.html',
  styleUrls: ['./user-home.component.sass'],
})
export class UserHomeComponent implements OnDestroy, AfterViewInit {
  subscription: Subscription = new Subscription();
  userRole: UserRoles = UserRoles.aguacate_client;
  user: any;
  nombreUsuario: string = '';
  userEnum = UserRoles;
  estadoValidacion: number = -1;

  // ── Cupo de registro (PRE operativo) ────────────────────────────────
  /** Cupo propio del cliente (`GET /cupo-registro/me`). */
  cupoMe: CupoRegistroMe | null = null;
  /** PRE con cupo válido y desbloqueado → opera como aprobado. */
  esPreOperativo = false;
  /** Sigue disponible el wizard de registro (mientras no sea aprobado pleno). */
  registroPendiente = false;

  /**
   * Número de la emisión del store que está corriendo.
   *
   * El callback de la suscripción es `async` y espera varias llamadas de red,
   * así que dos emisiones se solapan. La rama de NO aprobado espera una
   * llamada MÁS que la de aprobado (`cupoRegistroService.ensureMe()`), y por
   * eso termina siempre última: al reanudar pisaba el estado que ya había
   * escrito la emisión buena, y un cliente aprobado terminaba viendo
   * "tu información está siendo validada".
   *
   * Cada emisión toma un número y sólo escribe si sigue siendo la última.
   */
  private authTick = 0;

  /**
   * Ya se pidió una relectura del usuario para esta persona.
   *
   * La aprobación automática la escribe el cron 2–4 min después del alta, y
   * `intentarAprobacionAutomatica` sólo corre en el login: el cliente que se
   * quedó logueado en ese rato conserva el estado viejo en el store y —desde
   * que la sesión del cliente no caduca por inactividad— puede conservarlo
   * durante horas. Se releen los datos una sola vez por instancia.
   *
   * Sólo para PERSONA: el registro de empresa y sus restricciones no se tocan.
   */
  private relecturaPersonaPedida = false;

  /** Mostrar banner del tope (PRE con tope vigente). */
  get cupoAplicaTope(): boolean {
    return this.cupoMe?.aplicaTope === true;
  }

  /** Acceso suspendido por revisión (PRE bloqueado). */
  get cupoBloqueado(): boolean {
    return this.cupoMe?.bloqueado === true;
  }

  get cupoPorcentaje(): number {
    const p = this.cupoMe?.porcentajeUtilizado;
    return typeof p === 'number' ? p : 0;
  }

  /** Verde <70% · ámbar 70–99% · rojo ≥100%. */
  get cupoBarColor(): string {
    const p = this.cupoPorcentaje;
    if (p >= 100) return '#dc2626';
    if (p >= 70) return '#f59e0b';
    return '#16a34a';
  }

  /** CLP entero con separador de miles (es-CL). */
  formatCupoClp(value: number | null | undefined): string {
    if (value === null || value === undefined) return '—';
    return Math.round(value).toLocaleString('es-CL');
  }

  saldoActual = 0;
  sobregiroDisponible: number | undefined = 0;
  saldoContable: number | undefined = 0;
  lineaCreditoBloqueada: boolean | undefined = false;
  lineaCredito: number | undefined = 0;
  userEmail: string = '';
  userEmail2: string = '';
  isCompany = 0;
  saldoUsdt: number = 0;
  saldoUsdtTotal: number = 0;
  saldoUsdtBloqueado: number = 0;
  saldoUsdtDisponible: number = 0;
  saldoVes: number = 0;
  tipoId: string = '';

  /**
   * Chileno con saldo VES: suma la tarjeta y las acciones de bolívares sin
   * perder nada de lo CLP (los `tipoId === 'VE'` del home significan "moneda
   * local" y no cambian).
   */
  get esChilenoConVes(): boolean {
    // También CCVE/V o un tipo vacío con cédula V/E: tienen saldo VES en Nest
    // (`puedeTenerSaldoVes`) pero operan con el home CL.
    return this.tipoId !== 'VE' && tieneSaldoVes(this.tipoId, this.user?.numeroId);
  }

  /** Comprar bolívares con CLP: Nest la permite solo a la familia CL. */
  get puedeComprarBolivares(): boolean {
    return esTitularVesChileno(this.tipoId);
  }

  // Vita demo button visibility
  vitaServiceEnabled: boolean = false;
  vitaButtonVisible: boolean = false;
  vitaActiveCountriesCount: number = 0;

  /**
   * Estado del bloqueo por solicitud de actualización de datos. Cuando
   * el cliente tiene una solicitud abierta, en lugar de mostrar el
   * dashboard normal embebemos `<app-data-update-request>` con el
   * `requestId` para guiarlo a completar/ver el estado de la revisión.
   */
  hasPendingUpdate: boolean = false;
  pendingUpdateRequestId: string | null = null;

  /**
   * `true` cuando ya tenemos respuesta del primer `ensureLoaded()`.
   * Mientras está en `false`, el template muestra un loader en lugar
   * del dashboard normal — así evitamos que el cliente vea botones
   * operativos por unos ms antes de que llegue el status real y el
   * embed lo bloquee.
   *
   * Aplica solo para clientes. Para operadores, el flag queda en
   * `false` pero el template renderiza directo (no hay bloqueo).
   */
  statusInitialized: boolean = false;

  isModalOpen = false;
  movimientosVes: VesMovementItem[] = [];
  currentPageVes: number = 1;
  pageSizeVes: number = 10;
  totalMovimientosVes: number = 0;
  totalPagesVes: number = 0;
  loadingVes: boolean = false;
  expandedVesIndex: number | null = null;
  movimientosUsdt: WithdrawalHistoryItem[] = [];
  movimientosUsdtCardex: UsdtCardexItem[] = [];
  usdtWithdrawals: UsdtWithdrawalItem[] = [];
  usdtPendingSwaps: UsdtPendingSwapItem[] = [];
  unifiedUsdtMovements: UnifiedUsdtMovement[] = [];
  currentPageUsdt: number = 1;
  pageSizeUsdt: number = 10;
  totalMovimientosUsdt: number = 0;
  loadingUsdt: boolean = false;
  expandedUsdtIndex: number | null = null;

  ngAfterViewInit() {
    window.scrollTo(0, 0);
  }

  /**
   * Determina si mostrar el botón de Vita Wallet en el home:
   *  - Oculto si el flag backend VITA_SERVICIO = false
   *  - Visible si el servicio está habilitado (en todos los ambientes)
   */
  private loadVitaButtonVisibility(): void {
    this.subscription.add(
      this.vitaWalletService.getServiceStatus().subscribe({
        next: (res) => {
          this.vitaServiceEnabled = !!res?.enabled;
          this.vitaButtonVisible = this.vitaServiceEnabled;
          if (this.vitaButtonVisible) this.loadVitaActiveCountriesCount();
        },
        error: () => {
          this.vitaServiceEnabled = false;
          this.vitaButtonVisible = false;
        },
      }),
    );
  }

  private loadVitaActiveCountriesCount(): void {
    this.subscription.add(
      this.vitaCalculatorService.listCountries().subscribe({
        next: (list) => {
          this.vitaActiveCountriesCount = list?.length || 0;
        },
        error: () => {
          this.vitaActiveCountriesCount = 0;
        },
      }),
    );
  }

  get vitaButtonLabel(): string {
    // "Otros destinos" ahora soporta origen USDT y CLP (clientes CL); el origen
    // se elige dentro de la calculadora. Label genérico para VE y CL.
    return 'Enviar a otros destinos';
  }

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private cartolaService: CartolaService,
    private actionsService: ActionsService,
    private spinner: SpinnerService,
    private binanceDepositService: BinanceDepositService,
    private binanceWithdrawalService: BinanceWithdrawalService,
    private walletVesClientService: WalletVesClientService,
    private vitaWalletService: VitaWalletService,
    private vitaCalculatorService: VitaCalculatorService,
    private dataUpdateRequestService: DataUpdateRequestService,
    private cupoRegistroService: CupoRegistroService,
  ) {
    this.loadVitaButtonVisibility();

    // Suscripción reactiva al estado de bloqueo. Si el cliente tiene una
    // solicitud de actualización abierta, el componente embebe el form
    // en lugar del dashboard normal. Cuando el operador aprueba todo y
    // el status pasa a `bloqueado=false`, esta suscripción libera la
    // vista automáticamente sin que el cliente tenga que recargar.
    //
    // Importante: NO disparamos `ensureLoaded()` aquí — el rol todavía
    // no está poblado en este punto del constructor. Lo disparamos en
    // el callback de auth abajo, sólo si el usuario es cliente.
    this.subscription.add(
      this.dataUpdateRequestService.status$.subscribe((status) => {
        this.hasPendingUpdate = !!status?.bloqueado;
        this.pendingUpdateRequestId = status?.requestId ?? null;
        // Defensiva: si el backend marca bloqueo pero no devuelve el
        // `requestId` en el /me, intentamos resolverlo via el endpoint
        // "por usuario" para que el banner pueda navegar al form.
        if (this.hasPendingUpdate && !this.pendingUpdateRequestId) {
          this.resolveRequestIdFromUser();
        }
      }),
    );

    let spinnerRef = this.spinner.start();
    this.subscription.add(
      this.store.select('auth').subscribe(async ({ user, userRole }: any) => {
        const tick = ++this.authTick;
        try {
          this.userRole = userRole;
          this.user = user;
          // Re-evaluar visibilidad del rail "otros destinos" ahora que ya
          // tenemos el usuario: depende del flag backend VITA_SERVICIO.
          this.loadVitaButtonVisibility();
          console.log('Usuario logueado:', { user, userRole });
          this.tipoId = user?.tipoId || '';
          this.isCompany = user?.tipoUsuarioID >= 1 ? 1 : 0;
          this.userEmail = user?.email;
          if (this.userEmail) {
            this.userEmail2 = await this.generateCod(
              this.userEmail.toLocaleLowerCase().toString(),
            );
          }

          if (this.userRole === UserRoles.aguacate_client) {
            // Espera el primer fetch del status ANTES de bajar el flag
            // — así el template no muestra el dashboard normal mientras
            // todavía no sabemos si el cliente está bloqueado.
            try {
              await this.dataUpdateRequestService.ensureLoaded();
            } finally {
              this.statusInitialized = true;
            }

            // Polling continuo: detecta cuando el operador crea una
            // solicitud nueva mientras el cliente está logueado. Sin esto
            // el cliente sigue operando con status stale = false.
            this.startStatusPolling();

            // Mismo campo que mira el `ActivateGuard`. Antes acá se leía sólo
            // la relación `estadoCompliance`, así que con la relación ausente
            // el guard dejaba pasar al cliente y esta pantalla lo mandaba
            // igual a "en validación".
            const estadoCompliance = leerEstadoCompliance(user);
            // Aprobado (3) o bloqueado (4): el bloqueado opera igual que un
            // aprobado; solo sus depósitos por cartola no se acreditan solos.
            const esAprobado = operaComoAprobado(user);
            const esPersona = user?.tipoUsuarioID !== 1;

            // Persona que el store todavía muestra sin aprobar: puede ser un
            // dato viejo (la aprobación automática entró después del login).
            // Se pide la relectura y se corta acá — la emisión que traiga el
            // dato fresco es la que pinta. Empresa no entra: su flujo y sus
            // restricciones quedan exactamente como están.
            if (
              !esAprobado &&
              esPersona &&
              this.isNotNullOrEmpty(user) &&
              user?.email &&
              !this.relecturaPersonaPedida
            ) {
              this.relecturaPersonaPedida = true;
              this.store.dispatch(
                loadUser({ email: user.email, role: this.userRole }),
              );
              return;
            }

            // Cupo propio: si aún no está aprobado por compliance,
            // evaluamos si es PRE operativo (puede operar dentro de su
            // tope). Fail-closed: si el cupo no se puede leer, no habilita.
            this.esPreOperativo = false;
            this.cupoMe = null;
            if (!esAprobado) {
              const cupo = await this.cupoRegistroService.ensureMe();
              if (tick !== this.authTick) return;
              this.cupoMe = cupo;
              this.esPreOperativo = cupo?.puedeOperar === true;
            }
            // El wizard de registro sigue disponible mientras no sea
            // aprobado pleno (compliance === 3), aunque ya pueda operar.
            this.registroPendiente = !esAprobado;

            // Puede usar TODO lo de un aprobado: aprobado pleno o PRE operativo.
            const funcionalidadesAprobado = esAprobado || this.esPreOperativo;

            if (tick !== this.authTick) return;

            if (funcionalidadesAprobado) {
              this.estadoValidacion = EstadosUsuario.validado;
              await this.obtenerSaldo();
              if (this.tipoId === 'VE') {
                this.obtenerSaldoVes();
                this.obtenerMovimientosVes();
                this.obtenerMovimientosUsdt();
              } else if (this.esChilenoConVes) {
                // Sin movimientos VES el saldo es 0 (no hay fila en el cardex).
                this.obtenerSaldoVes();
              }
              return;
            }

            // No operativo: conserva el flujo previo (incompleto / validando).
            if (
              !this.isNotNullOrEmpty(user) ||
              (estadoCompliance === 0 && user.tipoUsuarioID === 1)
            ) {
              this.estadoValidacion = EstadosUsuario.incompleto;
              return;
            }

            if (estadoCompliance !== null && [0, 1, 2].includes(estadoCompliance)) {
              this.estadoValidacion = EstadosUsuario.pendienteValidacion;
            }
          }
        } catch (error) {
        } finally {
          this.spinner.stop(spinnerRef);
        }
      }),
    );

    this.subscription.add(
      this.actionsService.subjectSaldo.subscribe((res) => {
        if (res === true) {
          this.obtenerSaldo();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.stopStatusPolling();
  }

  /**
   * Polling del status del bloqueo cada minuto. Necesario para detectar
   * que el operador acaba de crear una solicitud mientras el cliente
   * está logueado. Sin esto, el cliente queda con cache `bloqueado=false`
   * indefinidamente hasta cerrar sesión.
   *
   * El componente embebido `DataUpdateRequestComponent` también tiene
   * su propio polling, pero ese sólo arranca cuando ya está bloqueado.
   * Este polling cubre la transición `false → true`.
   */
  private statusPollHandle: any = null;
  private static readonly STATUS_POLL_MS = 60_000;

  private startStatusPolling(): void {
    if (this.statusPollHandle) return;
    this.statusPollHandle = setInterval(() => {
      this.dataUpdateRequestService.refresh();
    }, UserHomeComponent.STATUS_POLL_MS);
  }

  private stopStatusPolling(): void {
    if (this.statusPollHandle) {
      clearInterval(this.statusPollHandle);
      this.statusPollHandle = null;
    }
  }


  /**
   * Mostrar el banner-CTA "Es requerido completar una validación de
   * datos" al top del home. El cliente ve el dashboard normal debajo
   * (con la limitación HTTP del interceptor). Al click en el CTA se
   * navega al form dedicado en `/wallet/client/data-update-request/:id`.
   */
  get shouldShowValidationBanner(): boolean {
    return this.hasPendingUpdate && !!this.pendingUpdateRequestId;
  }

  /**
   * Cliente que todavía no opera como aprobado (por ejemplo, en estado 0)
   * con una solicitud de actualización de datos abierta: ve solo la
   * solicitud (el aviso de arriba), no el botón de completar registro ni
   * el "en validación". Sin solicitud, sigue su registro normal. Un
   * aprobado (o un pre-aprobado que ya opera) ve su home con el aviso.
   */
  get soloSolicitudDeDatos(): boolean {
    return this.hasPendingUpdate && this.estadoValidacion !== EstadosUsuario.validado;
  }

  /**
   * Navegación al form dedicado (no embed). El formulario se abre en
   * full page con el botón "Volver al inicio" — el cliente completa la
   * validación y vuelve al home cuando termina.
   */
  goToValidationForm(): void {
    if (!this.pendingUpdateRequestId) return;
    this.router.navigate([
      '/wallet/client/data-update-request',
      this.pendingUpdateRequestId,
    ]);
  }

  /**
   * Caso defensivo: el backend marca `bloqueado=true` pero no devuelve
   * `requestId` y tampoco pudimos resolverlo via `getRequestByUser`
   * (endpoint inaccesible o sin solicitud activa). Mostramos pantalla
   * de "contactá soporte" en lugar de dejar al cliente en limbo.
   *
   * `requestIdResolving` evita parpadear el fallback mientras estamos
   * intentando resolver el id en background.
   */
  get shouldShowBlockedFallback(): boolean {
    return (
      this.hasPendingUpdate &&
      !this.pendingUpdateRequestId &&
      !this.requestIdResolving
    );
  }

  /**
   * Intenta obtener el `requestId` activo del usuario llamando al
   * endpoint `/data-update-requests/user/:usuarioId`. Se dispara cuando
   * el `/me` marca bloqueo sin id — best-effort, falla silencioso. Si
   * funciona, el embed se monta normalmente; si no, el fallback de
   * WhatsApp queda como red de seguridad.
   */
  private requestIdResolving: boolean = false;
  private async resolveRequestIdFromUser(): Promise<void> {
    if (this.requestIdResolving) return;
    if (!this.user?.usuarioId) return;
    this.requestIdResolving = true;
    try {
      const req = await this.dataUpdateRequestService.getRequestByUser(
        this.user.usuarioId,
      );
      if (req?.id) {
        this.pendingUpdateRequestId = req.id;
      }
    } catch {
      // Endpoint puede ser sólo de operador o no existir todavía;
      // fail-silent y el fallback de WhatsApp toma el relevo.
    } finally {
      this.requestIdResolving = false;
    }
  }

  /**
   * Abre WhatsApp con el mismo mensaje que usa la pantalla de
   * `maxRejects` del embed — flujo consistente cuando el cliente
   * necesita ayuda con su solicitud.
   */
  openWhatsAppSupport(): void {
    const phone = '56965474761';
    const message = 'Necesito ayuda con mi solicitud de actualizacion de datos';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async generateCod(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  isNotNullOrEmpty(object: Object) {
    return object && Object.keys(object).length > 0;
  }

  async obtenerSaldo() {
    if (this.user?.usuarioId) {
      let spinnerRef = this.spinner.start();
      try {
        const {
          saldo,
          lineaCreditoId,
          balancePermitido,
          lineaBloqueada,
          sobregiro,
        } = await this.cartolaService.getSaldoCliente(this.user.usuarioId);
        this.saldoActual = saldo;
        this.sobregiroDisponible = sobregiro;
        this.saldoContable = balancePermitido;
        this.lineaCreditoBloqueada = lineaBloqueada;
        this.lineaCredito = lineaCreditoId;
        await this.obtenerSaldoUsdt();
      } catch (error) {
      } finally {
        this.spinner.stop(spinnerRef);
      }
    }
  }

  async obtenerSaldoUsdt() {
    try {
      const balanceObservable =
        await this.binanceWithdrawalService.getBalance();
      const balance: BalanceResponse =
        await firstValueFrom(balanceObservable);

      this.saldoUsdtTotal = balance.totalBalance || 0;
      this.saldoUsdtBloqueado = balance.blockedBalance || 0;
      this.saldoUsdtDisponible = balance.availableBalance || 0;
      this.saldoUsdt = balance.availableBalance || 0;
    } catch (error) {
      this.saldoUsdtTotal = 0;
      this.saldoUsdtBloqueado = 0;
      this.saldoUsdtDisponible = 0;
      this.saldoUsdt = 0;
    }
  }

  obtenerSaldoVes(): void {
    this.walletVesClientService.getMyBalance().subscribe({
      next: (res) => {
        if (res?.success) {
          // Un usuario sin movimientos VES puede venir sin `data`.
          this.saldoVes = Number(res.data?.balanceVes) || 0;
        }
      },
      error: () => {
        this.saldoVes = 0;
      },
    });
  }

  obtenerMovimientosVes(page: number = 1): void {
    this.loadingVes = true;
    this.walletVesClientService
      .getMyMovements({ page, limit: this.pageSizeVes })
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.movimientosVes = res.data.items;
            this.totalMovimientosVes = res.data.total;
            this.totalPagesVes = res.data.totalPages;
            this.currentPageVes = res.data.page;
          }
          this.loadingVes = false;
        },
        error: () => {
          this.movimientosVes = [];
          this.loadingVes = false;
        },
      });
  }

  onPageChangeVes(event: PageEvent): void {
    this.currentPageVes = event.pageIndex + 1;
    this.pageSizeVes = event.pageSize;
    this.obtenerMovimientosVes(this.currentPageVes);
  }

  sanitizeVesDescription(mov: VesMovementItem): string {
    const desc = mov.description || '';
    // Strip operator email from description: "... por operador: email@x.com | Motivo: ..."
    // → "... por operador | Motivo: ..."
    const sanitized = desc.replace(/:\s*[\w.-]+@[\w.-]+\.\w+/g, '');
    if (sanitized.trim()) return sanitized.trim();
    // Fallback based on type
    if (mov.type) {
      const typeLabels: { [key: string]: string } = {
        CREDITO_MANUAL: 'Crédito manual VES',
        DEBITO_MANUAL: 'Débito manual VES',
        MANUAL_CREDIT: 'Crédito manual VES',
        MANUAL_DEBIT: 'Débito manual VES',
        SWAP_IN: 'Conversión recibida',
        SWAP_OUT: 'Conversión enviada',
        SWAP_USDT_VES: 'Swap USDT→VES',
        COMPRA_USDT_VES: 'Compra USDT (VES)',
        PAGO_MOVIL: 'Pago móvil',
        AJUSTE: 'Ajuste de saldo',
        RETIRO_VES: 'Retiro VES',
        REVERSO_RETIRO_VES: 'Reverso Retiro VES',
        ENVIO_VES_BENEFICIARIO: 'Envío VES Beneficiario',
        RETIRO_USDT_VES: 'Retiro USDT (VES)',
      };
      return typeLabels[mov.type] || mov.type;
    }
    return mov.creditVes && mov.creditVes > 0 ? 'Credito VES' : 'Debito VES';
  }

  getVesStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
      reversed: 'Reversado',
    };
    return labels[status?.toLowerCase()] || status || 'Sin estado';
  }

  getVesStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      completed: '#16a34a',
      approved: '#16a34a',
      rejected: '#dc2626',
      cancelled: '#6b7280',
      reversed: '#8b5cf6',
    };
    return colors[status?.toLowerCase()] || '#6b7280';
  }

  getVesTypeLabel(mov: VesMovementItem): string {
    if (mov.creditVes > 0) return 'Ingreso';
    return 'Egreso';
  }

  async obtenerMovimientosUsdt(page: number = 1): Promise<void> {
    this.loadingUsdt = true;
    if (this.tipoId === 'VE') {
      this.walletVesClientService
        .getMyUsdtMovements(page, this.pageSizeUsdt)
        .subscribe({
          next: (res) => {
            if (res.success) {
              this.movimientosUsdtCardex = res.data.items;
              this.usdtWithdrawals = res.data.withdrawals || [];
              this.usdtPendingSwaps = res.data.pendingSwaps || [];
              this.totalMovimientosUsdt = res.data.total;
              this.currentPageUsdt = res.data.page;
              this.buildUnifiedUsdtList();
            }
            this.loadingUsdt = false;
          },
          error: () => {
            this.movimientosUsdtCardex = [];
            this.usdtWithdrawals = [];
            this.usdtPendingSwaps = [];
            this.loadingUsdt = false;
          },
        });
    } else {
      try {
        const obs = await this.binanceWithdrawalService.getWithdrawalHistory(page, this.pageSizeUsdt);
        const res = await firstValueFrom(obs);
        this.movimientosUsdt = res.items;
        this.totalMovimientosUsdt = res.total;
        this.currentPageUsdt = res.page;
      } catch {
        this.movimientosUsdt = [];
      } finally {
        this.loadingUsdt = false;
      }
    }
  }

  onPageChangeUsdt(event: PageEvent): void {
    this.currentPageUsdt = event.pageIndex + 1;
    this.pageSizeUsdt = event.pageSize;
    this.obtenerMovimientosUsdt(this.currentPageUsdt);
  }

  getUsdtStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  }

  getUsdtStatusClass(status: string): string {
    const classes: { [key: string]: string } = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      completed: '#16a34a',
      rejected: '#dc2626',
      cancelled: '#6b7280',
    };
    return classes[status] || '#6b7280';
  }

  buildUnifiedUsdtList(): void {
    const fromCardex: UnifiedUsdtMovement[] = this.movimientosUsdtCardex.map(mov => ({
      source: 'cardex' as const,
      date: mov.createdAt,
      label: this.getCardexNetworkLabel(mov.network),
      amount: mov.haber > 0 ? mov.haber : mov.debe,
      isCredit: mov.haber > 0,
      status: 'completed',
      haber: mov.haber,
      debe: mov.debe,
      saldo: mov.saldo,
      description: mov.description,
      network: mov.network,
    }));

    const fromWithdrawals: UnifiedUsdtMovement[] = this.usdtWithdrawals.map(w => ({
      source: 'withdrawal' as const,
      date: w.requestedAt || w.createdAt,
      label: `Retiro ${w.network}`,
      amount: w.requestedAmount,
      isCredit: false,
      status: w.status,
      walletAddress: w.walletAddress,
      fee: w.fee,
      netAmount: w.netAmount,
      network: w.network,
      binanceTxId: w.binanceTxId,
      rejectionReason: w.rejectionReason,
    }));

    this.unifiedUsdtMovements = [...fromCardex, ...fromWithdrawals]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }

  sanitizePendingSwapDescription(swap: UsdtPendingSwapItem): string {
    const desc = swap.description || '';
    // Extract "Swap X USDT → Y VES" from description
    const swapMatch = desc.match(/Swap\s+([\d.]+)\s+USDT\s*→\s*([\d.,]+)\s*VES/i);
    if (swapMatch) {
      return `Swap ${swapMatch[1]} USDT → ${swapMatch[2]} VES`;
    }
    const typeLabels: { [key: string]: string } = {
      SWAP_USDT_VES: 'Swap USDT → VES',
      COMPRA_USDT_VES: 'Compra USDT (VES)',
      RETIRO_USDT_VES: 'Retiro USDT (VES)',
    };
    return typeLabels[swap.type] || swap.type || 'Operación pendiente';
  }

  getCardexNetworkLabel(network: string): string {
    const labels: { [key: string]: string } = {
      SWAP_USDT_VES: 'Swap USDT→VES',
      REVERSO_SWAP_USDT_VES: 'Reverso Swap USDT→VES',
      SWAP_VES_TO_USDT: 'Compra USDT (VES)',
      COMPRA_USDT_VES: 'Compra USDT (VES)',
      SEND_USDT: 'Envío USDT',
      DEPOSIT: 'Depósito',
      CREDITO_MANUAL: 'Crédito Manual',
      DEBITO_MANUAL: 'Débito Manual',
      MANUAL_CREDIT: 'Crédito Manual',
      MANUAL_DEBIT: 'Débito Manual',
      ADD_USDT_MANUAL: 'Crédito Manual USDT',
      DEBIT_USDT_MANUAL: 'Débito Manual USDT',
      TRX: 'Depósito TRX',
      RETIRO_USDT_VES: 'Retiro USDT (VES)',
    };
    return labels[network] || network;
  }

  getWithdrawalStatusLabel(status: string): string {
    const labels: { [key: string]: string } = {
      pending: 'Pendiente',
      processing: 'Procesando',
      completed: 'Completado',
      rejected: 'Rechazado',
      cancelled: 'Cancelado',
    };
    return labels[status] || status;
  }

  getWithdrawalStatusColor(status: string): string {
    const colors: { [key: string]: string } = {
      pending: '#f59e0b',
      processing: '#3b82f6',
      completed: '#16a34a',
      rejected: '#dc2626',
      cancelled: '#6b7280',
    };
    return colors[status] || '#6b7280';
  }

  /**
   * 30/10/2026: rótulo que separa, en los modales de Recargar y Retirar del
   * chileno con saldo VES, sus opciones de bolívares de las de CLP y USDT.
   */
  private static readonly ROTULO_BOLIVARES = `
          <div style="display: flex; align-items: center; gap: 10px; margin-top: 4px;">
            <span style="font-size: 0.72rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em;">Bolívares</span>
            <span style="flex: 1; height: 1px; background: #e5e7eb;"></span>
          </div>`;

  openRechargeModal() {
    // 30/10/2026: el chileno con saldo VES (`esChilenoConVes`) suma aquí sus
    // opciones para sumar bolívares, que antes eran botones de texto en la
    // tarjeta VES (ahora solo muestra el saldo): comprarlos con sus pesos (solo
    // si `puedeComprarBolivares`) y convertir USDT a VES, con los íconos de las
    // opciones VES del venezolano. Recargar bolívares NO: los chilenos no pueden
    // recargar VES, así que ninguna opción lleva a `rechargeAccount` con
    // `moneda: 'VES'`. Para el resto el modal queda igual.
    const opcionesVes = !this.esChilenoConVes ? '' : [
      UserHomeComponent.ROTULO_BOLIVARES,
      !this.puedeComprarBolivares ? '' : `
          <button id="btn-cl-comprar-ves" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/chile.png" alt="CLP" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Comprar bolívares</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Con tus pesos (CLP → VES)</div>
            </div>
            <img src="assets/flags/venezuela.png" alt="VES" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex-shrink: 0;">
          </button>`,
      `
          <button id="btn-cl-usdt-ves" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Convertir USDT a VES</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">USDT → VES</div>
            </div>
            <img src="assets/flags/venezuela.png" alt="VES" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex-shrink: 0;">
          </button>`,
    ].join('');
    Swal.fire({
      title: '<span style="color: #1e5a3f; font-weight: 800;">Recargar</span>',
      html: `
        <p style="margin-bottom: 1rem; color: #6b7280; font-size: 0.85rem;">Selecciona el tipo de recarga</p>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="btn-clp" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/chile.png" alt="CLP" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Recargar Saldo</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Pesos chilenos (CLP)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
          <button id="btn-usdt" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Recargar USDT</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Tether (USDT)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>${opcionesVes}
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonColor: '#6b7280',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.querySelector('#btn-clp')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/rechargeAccount']);
          });
          popup.querySelector('#btn-usdt')?.addEventListener('click', () => {
            Swal.close();
            // Navegamos con el flag para que `load-pays-usdt` abra el modal
            // de depósito de una: el cliente ya expresó la intención al
            // elegir "Recargar USDT" aquí.
            this.router.navigate(['/wallet/client/load-pays-usdt'], {
              state: { openUsdtRechargeModal: true },
            });
          });
          // Opciones VES del chileno: solo están si se pintaron arriba.
          popup.querySelector('#btn-cl-comprar-ves')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/comprar-bolivares']);
          });
          popup.querySelector('#btn-cl-usdt-ves')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/convert-usdt-ves']);
          });
        }
      },
    });
  }

  openConversionVeModal() {
    Swal.fire({
      title: '<span style="color: #1e5a3f; font-weight: 800;">Conversiones</span>',
      html: `
        <p style="margin-bottom: 1rem; color: #6b7280; font-size: 0.85rem;">Selecciona el tipo de operación</p>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="btn-ves-usdt" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/venezuela.png" alt="VES" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Comprar USDT</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">VES → USDT</div>
            </div>
            <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex-shrink: 0;">
          </button>
          <button id="btn-usdt-ves" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Convertir USDT a VES</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">USDT → VES</div>
            </div>
            <img src="assets/flags/venezuela.png" alt="VES" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex-shrink: 0;">
          </button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonColor: '#6b7280',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.querySelector('#btn-ves-usdt')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/convert-ves-usdt']);
          });
          popup.querySelector('#btn-usdt-ves')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/convert-usdt-ves']);
          });
        }
      },
    });
  }

  openRechargeVeModal() {
    Swal.fire({
      title: '<span style="color: #1e5a3f; font-weight: 800;">Recargar</span>',
      html: `
        <p style="margin-bottom: 1rem; color: #6b7280; font-size: 0.85rem;">Selecciona el tipo de recarga</p>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="btn-ves" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/venezuela.png" alt="VES" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Recargar Saldo</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Bolívares (VES)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
          <button id="btn-usdt-ve" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Recargar USDT</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Tether (USDT)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonColor: '#6b7280',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.querySelector('#btn-ves')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/rechargeAccount']);
          });
          popup.querySelector('#btn-usdt-ve')?.addEventListener('click', () => {
            Swal.close();
            // Mismo flag que en el modal CL: entrar por "Recargar USDT"
            // abre directo el modal de depósito.
            this.router.navigate(['/wallet/client/load-pays-usdt'], {
              state: { openUsdtRechargeModal: true },
            });
          });
        }
      },
    });
  }

  openRetiroVeModal() {
    Swal.fire({
      title: '<span style="color: #1e5a3f; font-weight: 800;">Retirar</span>',
      html: `
        <p style="margin-bottom: 1rem; color: #6b7280; font-size: 0.85rem;">Selecciona el tipo de retiro</p>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="btn-ves-ret" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #fff7ed; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/venezuela.png" alt="VES" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Retirar Saldo</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Bolívares (VES)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
          <button id="btn-usdt-ve-ret" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Retirar USDT</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Tether (USDT)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonColor: '#6b7280',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.querySelector('#btn-ves-ret')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/withdraw-ves']);
          });
          popup.querySelector('#btn-usdt-ve-ret')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/send-usdt']);
          });
        }
      },
    });
  }

  openRetiroModal() {
    // 30/10/2026: el chileno con saldo VES suma aquí, bajo el rótulo
    // "Bolívares", las salidas de sus bolívares por las mismas pantallas del
    // venezolano (antes eran botones de texto en la tarjeta VES): retirarlos a
    // un banco de Venezuela o comprar USDT con ellos. VES → Chile no va aquí:
    // ya lo ofrece "Enviar Remesa" (origen VES), y no se duplican botones.
    // Mismos íconos que las opciones VES del venezolano. Para el resto el
    // modal queda igual.
    const opcionesVes = !this.esChilenoConVes ? '' : `${UserHomeComponent.ROTULO_BOLIVARES}
          <button id="btn-cl-ves-ret" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #fff7ed; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/venezuela.png" alt="VES" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Retirar Saldo</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Bolívares (VES)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
          <button id="btn-cl-ves-usdt" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/venezuela.png" alt="VES" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Comprar USDT</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">VES → USDT</div>
            </div>
            <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover; flex-shrink: 0;">
          </button>`;
    Swal.fire({
      title: '<span style="color: #1e5a3f; font-weight: 800;">Retirar</span>',
      html: `
        <p style="margin-bottom: 1rem; color: #6b7280; font-size: 0.85rem;">Selecciona el tipo de retiro</p>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button id="btn-clp" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #fff7ed; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/flags/chile.png" alt="CLP" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Retirar Saldo</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Pesos chilenos (CLP)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>
          <button id="btn-usdt" style="display: flex; align-items: center; gap: 14px; padding: 14px 16px; background: white; border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; cursor: pointer; transition: all 0.2s; text-align: left;" onmouseover="this.style.borderColor='#34d399'; this.style.background='#f0fdf4'" onmouseout="this.style.borderColor='#e5e7eb'; this.style.background='white'">
            <div style="width: 44px; height: 44px; border-radius: 12px; background: #f0fdf4; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
              <img src="assets/icons/USDT_Logo.png" alt="USDT" style="width: 26px; height: 26px; border-radius: 50%; object-fit: cover;">
            </div>
            <div style="flex: 1;">
              <div style="font-weight: 700; font-size: 0.9rem; color: #1e5a3f;">Retirar USDT</div>
              <div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">Tether (USDT)</div>
            </div>
            <span style="color: #9ca3af; font-size: 18px;">›</span>
          </button>${opcionesVes}
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonColor: '#6b7280',
      cancelButtonText: 'Cancelar',
      didOpen: () => {
        const popup = Swal.getPopup();
        if (popup) {
          popup.querySelector('#btn-clp')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/withdraw']);
          });
          popup.querySelector('#btn-usdt')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/send-usdt']);
          });
          // Opciones VES del chileno: solo están si se pintaron arriba.
          popup.querySelector('#btn-cl-ves-ret')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/withdraw-ves']);
          });
          popup.querySelector('#btn-cl-ves-usdt')?.addEventListener('click', () => {
            Swal.close();
            this.router.navigate(['/wallet/client/convert-ves-usdt']);
          });
        }
      },
    });
  }
}
